/**
 * 对话工作流 / 节点画布风格库
 * - GET /normal/categories
 * - POST /search  { page } 或 { category_id, page }
 * - 当按分类搜索为空时：用内置 uuid→分类名映射，从「全部」结果本地筛选（不依赖 category_relations）
 */

import api from '@/utils/api'
import styleCatMap from '@/data/oiioiiStyleCategoryMap.json'

/**
 * @param {object} it
 * @param {number} idx
 * @param {string} categoryId
 */
export function normalizeWorkflowStyleItem(it, idx, categoryId) {
  const cid = categoryId != null ? String(categoryId) : 'all'
  const o = it && typeof it === 'object' ? it : {}
  const id = o.id != null ? String(o.id) : `${cid}-${Date.now()}-${idx}`
  const uuid = o.uuid != null ? String(o.uuid) : ''
  const name = String(o.name || o.title || o.label || '未命名风格')
  const thumb = String(o.thumb || o.cover || o.image || o.url || '')
  const desc = String(o.desc || o.description || o.remark || '')
  const prompt = String(o.prompt || o.style_prompt || o.stylePrompt || '')
  const refDataUrl = String(o.refDataUrl || o.ref_data_url || '')
  const refUrl = String(o.refUrl || o.ref_url || '')
  const badge = o.badge != null ? String(o.badge) : ''
  return { id, uuid, tab: cid, name, thumb, desc, prompt, badge, refDataUrl, refUrl }
}

function pick(obj, keys) {
  if (!obj || typeof obj !== 'object') return null
  for (const k of keys) {
    if (obj[k] != null) return obj[k]
  }
  return null
}

function extractSearchList(res) {
  const data = res && res.data != null ? res.data : res
  const inner = data && data.data != null && !Array.isArray(data.data) ? data.data : data
  const list =
    (inner && Array.isArray(inner.list) && inner.list) ||
    (data && Array.isArray(data.list) && data.list) ||
    (res && Array.isArray(res.list) && res.list) ||
    []
  return { data: inner || data || {}, list: Array.isArray(list) ? list : [] }
}

/**
 * 合并一次 POST /search 的返回（分页、是否结束），与 ConversationWorkflow.fetchCategoryStyles 对齐
 */
export function mergeWorkflowStyleSearchResponse(res, opts) {
  const categoryId = opts && opts.categoryId != null ? String(opts.categoryId) : ''
  const reset = !!(opts && opts.reset)
  const cur = (opts && opts.previousBucket) || null
  const cid = categoryId || 'all'

  const { data, list } = extractSearchList(res)
  const normalized = list.map((it, idx) => normalizeWorkflowStyleItem(it, idx, cid))
  const prev = reset ? [] : cur && Array.isArray(cur.list) ? cur.list : []
  // 按 id/uuid 去重，避免接口忽略 page 时重复追加导致列表跳动
  const seen = new Set()
  const merged = []
  for (let i = 0; i < prev.length; i++) {
    const x = prev[i]
    const k = x && (x.uuid || x.id) != null ? String(x.uuid || x.id) : ''
    if (k) {
      if (seen.has(k)) continue
      seen.add(k)
    }
    merged.push(x)
  }
  let appended = 0
  for (let i = 0; i < normalized.length; i++) {
    const x = normalized[i]
    const k = x && (x.uuid || x.id) != null ? String(x.uuid || x.id) : ''
    if (k) {
      if (seen.has(k)) continue
      seen.add(k)
    }
    merged.push(x)
    appended += 1
  }
  const nextPage = reset ? 1 : Math.max(1, parseInt(cur && cur.page, 10) || 1)

  let finished = normalized.length === 0
  let finishedReason = finished ? 'empty_list' : ''
  // 本页全部是重复项，视为没有更多
  if (!finished && !reset && appended === 0 && normalized.length > 0) {
    finished = true
    finishedReason = 'all_duplicates'
  }

  try {
    const pageMeta =
      (data && (data.pagination || data.paging || data.page_meta || data.pageMeta || data.meta)) || null

    const hasMore = pick(data, ['has_more', 'hasMore']) ?? pick(pageMeta, ['has_more', 'hasMore'])
    if (hasMore === false || hasMore === 0 || hasMore === '0') {
      finished = true
      finishedReason = 'has_more=false'
    }
    const currentPage =
      pick(data, ['current_page', 'currentPage', 'page']) ?? pick(pageMeta, ['current_page', 'currentPage', 'page'])
    const lastPage =
      pick(data, ['last_page', 'lastPage', 'pages', 'total_pages', 'totalPages']) ??
      pick(pageMeta, ['last_page', 'lastPage', 'pages', 'total_pages', 'totalPages'])
    if (!finished && currentPage != null && lastPage != null && Number(currentPage) >= Number(lastPage)) {
      finished = true
      finishedReason = 'current>=last'
    }
    const nextUrl =
      pick(data, ['next_page_url', 'nextPageUrl']) ?? pick(pageMeta, ['next_page_url', 'nextPageUrl'])
    if (nextUrl === null) {
      if (
        (data &&
          (Object.prototype.hasOwnProperty.call(data, 'next_page_url') ||
            Object.prototype.hasOwnProperty.call(data, 'nextPageUrl'))) ||
        (pageMeta &&
          (Object.prototype.hasOwnProperty.call(pageMeta, 'next_page_url') ||
            Object.prototype.hasOwnProperty.call(pageMeta, 'nextPageUrl')))
      ) {
        finished = true
        finishedReason = 'next_url=null'
      }
    }

    const perPage =
      pick(data, ['per_page', 'perPage', 'page_size', 'pageSize', 'limit', 'page_limit', 'pageLimit']) ??
      pick(pageMeta, ['per_page', 'perPage', 'page_size', 'pageSize', 'limit', 'page_limit', 'pageLimit'])
    if (!finished && perPage != null && Number(perPage) > 0) {
      if (normalized.length < Number(perPage)) {
        finished = true
        finishedReason = 'len<per_page'
      }
    }

    // 注意：不要用 count（常为当页条数），只用明确的总数字段
    const total =
      pick(data, ['total', 'total_count', 'totalCount', 'item_total']) ??
      pick(pageMeta, ['total', 'total_count', 'totalCount', 'item_total'])
    if (!finished && total != null && Number(total) >= 0) {
      if (merged.length >= Number(total)) {
        finished = true
        finishedReason = 'merged>=total'
      }
    }
  } catch {
    // ignore
  }

  return {
    page: nextPage + 1,
    list: merged,
    finished,
    finishedReason
  }
}

export async function fetchWorkflowStyleCategoriesList() {
  const res = await api.get('/normal/categories')
  const list =
    (res && res.data && Array.isArray(res.data.list) && res.data.list) ||
    (res && Array.isArray(res.list) && res.list) ||
    []
  return Array.isArray(list) ? list : []
}

/** @type {object[]|null} */
let _allStylesRawCache = null

export function clearWorkflowStyleAllCache() {
  _allStylesRawCache = null
}

/**
 * 拉取「全部」风格原始列表（分页拼满，带缓存）
 */
export async function fetchAllWorkflowStyleRawItems({ force } = {}) {
  if (_allStylesRawCache && !force) return _allStylesRawCache
  const out = []
  let page = 1
  for (let guard = 0; guard < 50; guard++) {
    const res = await api.post('/search', { page })
    const { list } = extractSearchList(res)
    if (!list.length) break
    out.push.apply(out, list)
    if (list.length < 10) break
    page += 1
  }
  _allStylesRawCache = out
  return out
}

function uuidSetForCategoryTitle(title) {
  const t = String(title || '').trim()
  const set = new Set()
  const items = (styleCatMap && Array.isArray(styleCatMap.items) ? styleCatMap.items : []) || []
  for (let i = 0; i < items.length; i++) {
    const row = items[i]
    if (row && String(row.category_title || '').trim() === t && row.uuid) {
      set.add(String(row.uuid))
    }
  }
  return set
}

/**
 * 按分类拉风格：优先走后端；若第 1 页为空则用内置映射本地筛选「全部」
 */
export async function postWorkflowStyleSearch({ categoryId, page }) {
  const cid = categoryId != null ? String(categoryId) : 'all'
  const p = Math.max(1, Number(page) || 1)

  if (cid === 'all') {
    return api.post('/search', { page: p })
  }

  let serverRes = null
  try {
    serverRes = await api.post('/search', { category_id: cid, page: p })
    const { list } = extractSearchList(serverRes)
    if (list.length > 0 || p > 1) {
      return serverRes
    }
  } catch {
    // fall through to local
  }

  const cats = await fetchWorkflowStyleCategoriesList()
  const cat = (cats || []).find((c) => c && String(c.id) === cid)
  const title = cat ? String(cat.title || cat.name || cat.label || '') : ''
  const uuidSet = uuidSetForCategoryTitle(title)
  const all = await fetchAllWorkflowStyleRawItems()
  const filtered = all.filter((it) => it && uuidSet.has(String(it.uuid || '')))
  const pageSize = 20
  const start = (p - 1) * pageSize
  const slice = filtered.slice(start, start + pageSize)

  return {
    data: {
      list: slice,
      has_more: start + pageSize < filtered.length,
      item_total: filtered.length,
      _local_category_fallback: true
    }
  }
}

/**
 * 与 ConversationWorkflow 中 Seedream 出图一致：风格参考 URL（dataURL / http）
 * @param {object|null|undefined} style
 * @returns {string}
 */
export function pickWorkflowStyleRef(style) {
  if (!style || typeof style !== 'object') return ''
  return (
    (style.refDataUrl ? String(style.refDataUrl) : '').trim() ||
    (style.refUrl ? String(style.refUrl) : '').trim() ||
    (style.thumb ? String(style.thumb) : '').trim()
  )
}

/** 节点是否已配置可用风格（本节点 workflowStyle） */
export function isWorkflowStyleSet(style) {
  if (!style || typeof style !== 'object') return false
  return !!(
    (style.id != null && String(style.id).trim()) ||
    (style.name != null && String(style.name).trim()) ||
    (style.prompt != null && String(style.prompt).trim()) ||
    pickWorkflowStyleRef(style)
  )
}

/** @param {object|null|undefined} style */
export function cloneWorkflowStylePlain(style) {
  if (!isWorkflowStyleSet(style)) return null
  try {
    return JSON.parse(JSON.stringify(style))
  } catch {
    return { ...style }
  }
}

/** @param {object|null|undefined} sbNode */
export function workflowStyleFromStoryboardNode(sbNode) {
  if (
    !sbNode ||
    !sbNode.data ||
    (sbNode.type !== 'storyboard' && sbNode.type !== 'storyPanel')
  ) {
    return null
  }
  return cloneWorkflowStylePlain(sbNode.data.workflowStyle)
}

/**
 * 用户提示词是否明确要求出现人物/角色
 * @param {string} text
 */
export function promptMentionsSubjectPeople(text) {
  const t = String(text || '')
  if (!t.trim()) return false
  if (
    /人物|角色|男人|女人|男生|女生|男孩|女孩|帅哥|美女|女主|男主|老人|小孩|婴儿|人群|行人|路人|模特|演员|情侣|男女|少女|少年|大叔|阿姨|宝宝|孩子|人像|半身像|全身像/.test(
      t
    )
  ) {
    return true
  }
  return /\b(portrait|person|people|man|woman|boy|girl|character|human|face|crowd|couple|actor|actress)\b/i.test(t)
}

/**
 * 风格库 prompt 常含「人物审美/女主脸」等主体描述；无人物需求时只保留画风关键词
 * @param {string} stylePrompt
 * @param {{ allowPeople?: boolean }} [opts]
 */
export function sanitizeWorkflowStyleKeywords(stylePrompt, opts) {
  let sp = String(stylePrompt || '').trim()
  if (!sp) return ''
  const allowPeople = !!(opts && opts.allowPeople)
  if (allowPeople) {
    return sp.length > 420 ? sp.slice(0, 420) + '…' : sp
  }
  sp = sp
    .replace(/角色方面[：:][\s\S]*?(?=在场景|在构图|整体|色彩|色调|光影|滤镜|$)/g, '')
    .replace(/人物设计[\s\S]*?(?=打光|镜头|色彩|色调|光影|滤镜|整体|$)/g, '')
    .replace(/女性角色[\s\S]*?(?=男性角色|打光|镜头|色彩|整体|$)/g, '')
    .replace(/男性角色[\s\S]*?(?=打光|镜头|色彩|服装|整体|$)/g, '')
    .replace(/(?:女主脸|男主脸|韩系偶像|霸总|半写实偶像)[^。；;\n]*/g, '')
  const keepHints = sp.match(
    /(?:电影级|胶片|柔光|Bloom|HDR|霓虹|调色|光影|材质|渲染|滤镜|景深|体积光|高饱和|低饱和|冷调|暖调|写实|动漫|赛璐璐|厚涂|水彩|像素|3D|CG)[^，。；;\n]{0,24}/g
  )
  let out = ''
  if (keepHints && keepHints.length) {
    out = Array.from(new Set(keepHints)).slice(0, 18).join('，')
  } else if (!/人物|角色|女主|男主|肤色|妆容|五官|发型|面孔|人脸/.test(sp)) {
    out = sp
  }
  out = out.replace(/\s+/g, ' ').trim()
  if (out.length > 280) out = out.slice(0, 280) + '…'
  return out
}

/**
 * 【已选风格】元信息（给 LLM / 生图前置）
 * @param {object|null|undefined} style
 * @param {{ userPrompt?: string }} [opts]
 */
export function formatWorkflowStyleMeta(style, opts) {
  if (!style || typeof style !== 'object') return ''
  const styleName = style.name != null ? String(style.name) : ''
  const allowPeople = promptMentionsSubjectPeople(opts && opts.userPrompt)
  const stylePrompt = sanitizeWorkflowStyleKeywords(style.prompt, { allowPeople })
  const meta = []
  if (styleName) meta.push('StyleName=' + styleName)
  if (stylePrompt) meta.push('StylePrompt=' + stylePrompt)
  if (!meta.length) return ''
  const guard = allowPeople
    ? ''
    : '；SubjectLock=仅用风格的光影/色调/材质/渲染，禁止引入风格库中的人物/面孔/身份'
  return `【已选风格】${meta.join('；')}${guard}`
}

/**
 * @param {object|null|undefined} style 归一化风格对象
 * @param {{ userPrompt?: string }} [opts]
 * @returns {string}
 */
export function buildWorkflowStylePromptSuffix(style, opts) {
  if (!style || typeof style !== 'object') return ''
  const styleName = style.name != null ? String(style.name) : ''
  const userPrompt = opts && opts.userPrompt != null ? String(opts.userPrompt) : ''
  const allowPeople = promptMentionsSubjectPeople(userPrompt)
  const stylePrompt = sanitizeWorkflowStyleKeywords(style.prompt, { allowPeople })
  const styleRef = pickWorkflowStyleRef(style)
  if (!styleName && !stylePrompt && !styleRef) return ''

  const lines = [
    '',
    '',
    'STYLE REQUIREMENTS (must follow):',
    `- Apply ONLY the visual aesthetics of style "${styleName || 'custom style'}" (color grading, lighting, materials, rendering, film look).`,
    stylePrompt ? `- Style aesthetics keywords: ${stylePrompt}` : '',
    '- The attached style image (if any) is a STYLE REFERENCE only — NOT a subject / identity / composition reference.',
    '- Do NOT copy people, faces, body, pose, clothing, props-in-hand, or identity from the style reference image.',
    '- The MAIN text prompt defines the subject and scene. Follow it strictly.',
    allowPeople
      ? '- Character appearance may follow the style aesthetics, but identity/pose must come from the main prompt (or character refs), not the style thumbnail.'
      : '- CRITICAL: The main prompt has NO people. Output must contain ZERO humans / faces / characters / body parts. Empty environment / still life / scenery only. Ignore any people visible in the style reference.',
    '- Do NOT invent people just because the style name or style thumbnail shows a person.'
  ]
  return lines.filter((x, i) => i < 2 || x !== '').join('\n') + '\n'
}
